import {
    useEffect,
    useState,
} from "react";

import { createPortal } from "react-dom";

import Token from "./Token";

import {
    getPlayerPathVisualPosition,
    getPlayerPathIndex,
    getTokenGlobalPathIndex,
    isSafeCell,
} from "../game/boardPath";

import {
    HOME_POSITIONS,
    TOKEN_STATUS,
    TURN_ORDER,
} from "../game/gameConstants";

import {
    createInitialTokens,
} from "../game/createTokens";
import { calculateLudoWinning } from "./ludoPrize";


function LudoBoard({
    dice,
    rollId,
    activePlayers = [],
    gameMode,
    autoPlay,
    savedBoardState,
    playerProfiles,
    playSound,
    betAmount,
    entryBet,
    onPlayAgain,
    onRollAvailabilityChange,
    onCurrentPlayerChange,
    onGameStateChange,
}) {


    // ========================================
    // TOKENS
    // ========================================

    const [
        tokens,
        setTokens,
    ] = useState(() => {

        if (
            Array.isArray(
                savedBoardState?.tokens
            )
        ) {

            return savedBoardState.tokens;

        }

        return createInitialTokens();

    });


    // ========================================
    // CURRENT PLAYER
    // ========================================

    const [
        currentPlayer,
        setCurrentPlayer,
    ] = useState(
        savedBoardState?.currentPlayer ||
        activePlayers[0]
    );



    useEffect(() => {

        if (onCurrentPlayerChange) {

            onCurrentPlayerChange(
                currentPlayer
            );

        }

    }, [
        currentPlayer,
        onCurrentPlayerChange,
    ]);


    // ========================================
    // DICE HAS BEEN ROLLED
    // ========================================

    const [
        hasRolled,
        setHasRolled,
    ] = useState(
        savedBoardState?.hasRolled ?? false
    );

    // ========================================
    // SYNC DICE AVAILABILITY
    // ========================================

    useEffect(() => {

        if (!onRollAvailabilityChange) {
            return;
        }

        // Dice already rolled
        if (hasRolled) {

            onRollAvailabilityChange(false);

            return;
        }

        // Dice ready
        onRollAvailabilityChange(true);

    }, [
        hasRolled,
        onRollAvailabilityChange,
    ]);


    // ========================================
    // TOKEN IS MOVING
    // ========================================

    const [
        isMoving,
        setIsMoving,
    ] = useState(false);

    const [winner, setWinner] = useState(null);

    // ========================================
    // CHECK WINNER
    // ========================================

    useEffect(() => {
        if (winner) {
            return;
        }

        if (!activePlayers.length) {
            return;
        }

        for (const color of activePlayers) {

            const playerTokens = tokens.filter(
                token => token.color === color
            );

            const finishedTokens = playerTokens.filter(
                token =>
                    token.status === TOKEN_STATUS.FINISHED
            );

            // All 4 tokens finished
            if (finishedTokens.length === 4) {

                // 🔊 WINNER SOUND
                playSound("win");

                // console.log(
                //     `${color} has won the game`
                // );

                setWinner(color);

                // Stop everything immediately
                setIsMoving(false);
                setHasRolled(false);

                if (onRollAvailabilityChange) {
                    onRollAvailabilityChange(false);
                }

                return;
            }
        }

    }, [
        tokens,
        activePlayers,
        winner,
        onRollAvailabilityChange,
    ]);

    // ========================================
    // GET NEXT PLAYER
    // ========================================

    const getNextPlayer = (player) => {

        if (!activePlayers.length) {
            return player;
        }

        const currentIndex =
            activePlayers.indexOf(player);

        if (currentIndex === -1) {
            return activePlayers[0];
        }

        const nextIndex =
            (currentIndex + 1) %
            activePlayers.length;

        return activePlayers[nextIndex];
    };


    // ========================================
    // CHECK TOKEN VALIDITY
    // ========================================

    const canTokenMove = (
        token
    ) => {

        // Cannot move during animation
        if (isMoving) {
            return false;
        }


        // Wrong player
        if (
            token.color !==
            currentPlayer
        ) {
            return false;
        }


        // Dice must have been rolled
        if (!hasRolled) {
            return false;
        }


        // ======================================
        // HOME TOKEN
        // ======================================

        if (
            token.status ===
            TOKEN_STATUS.HOME
        ) {

            return dice === 6;
        }


        // ======================================
        // ACTIVE TOKEN
        // ======================================

        if (
            token.status ===
            TOKEN_STATUS.ACTIVE
        ) {

            const newStep =
                token.step + dice;


            // Cannot go beyond final position
            if (newStep > 56) {
                return false;
            }


            return true;
        }


        return false;
    };


    // ========================================
    // GET VALID TOKENS
    // ========================================

    const getValidTokens = (
        tokenList = tokens
    ) => {

        return tokenList.filter(
            token =>
                canTokenMove(token)
        );
    };

    // ========================================
    // NEW DICE ROLL EVENT
    // ========================================

    useEffect(() => {

        // rollId 0 means no new roll
        if (rollId === 0) {
            return;
        }

        setHasRolled(true);

    }, [rollId]);


    // ========================================
    // AUTO MOVE WHEN ONLY ONE TOKEN IS VALID
    // ========================================

    useEffect(() => {

        if (!hasRolled) {
            return;
        }

        if (isMoving) {
            return;
        }

        const validTokens =
            getValidTokens();


        // ======================================
        // ONLY ONE TOKEN CAN MOVE
        // ======================================

        if (
            validTokens.length === 1
        ) {

            const timer =
                setTimeout(() => {

                    animateToken(
                        validTokens[0]
                    );

                }, 300);


            return () => {
                clearTimeout(timer);
            };
        }


        // ======================================
        // NO TOKEN CAN MOVE
        // ======================================

        if (
            validTokens.length === 0
        ) {

            const timer =
                setTimeout(() => {

                    setHasRolled(false);

                    const nextPlayer =
                        getNextPlayer(
                            currentPlayer
                        );

                    setCurrentPlayer(
                        nextPlayer
                    );

                    onRollAvailabilityChange(
                        true
                    );

                }, 900);


            return () => {
                clearTimeout(timer);
            };
        }

    }, [
        hasRolled,
        isMoving,
        currentPlayer,
        dice,
        tokens,
    ]);


    // ========================================
    // UPDATE TOKEN POSITION
    // ========================================

    const updateTokenStep = (
        tokenId,
        step
    ) => {

        setTokens(
            currentTokens =>
                currentTokens.map(
                    token => {

                        if (
                            token.id !==
                            tokenId
                        ) {
                            return token;
                        }

                        return {
                            ...token,
                            status:
                                TOKEN_STATUS.ACTIVE,
                            step,
                        };

                    }
                )
        );

    };

    // ========================================
    // CAPTURE OPPONENT TOKENS
    // ========================================

    const captureOpponents = (
        movedTokenId,
        movedTokenColor,
        movedTokenStep,
        currentTokens
    ) => {

        // --------------------------------------
        // Only common path can capture
        // --------------------------------------

        if (
            movedTokenStep < 0 ||
            movedTokenStep >= 51
        ) {
            return {
                tokens: currentTokens,
                captured: false,
            };
        }


        // --------------------------------------
        // Get physical position
        // --------------------------------------

        const globalPosition =
            getPlayerPathIndex(
                movedTokenColor,
                movedTokenStep
            );


        if (
            globalPosition === null
        ) {
            return {
                tokens: currentTokens,
                captured: false,
            };
        }


        // --------------------------------------
        // SAFE CELL
        // --------------------------------------

        if (
            isSafeCell(globalPosition)
        ) {
            return {
                tokens: currentTokens,
                captured: false,
            };
        }


        // --------------------------------------
        // FIND OPPONENTS ON CELL
        // --------------------------------------

        const opponentTokens =
            currentTokens.filter(token => {

                // Don't capture yourself
                if (
                    token.id === movedTokenId
                ) {
                    return false;
                }

                // Don't capture same color
                if (
                    token.color === movedTokenColor
                ) {
                    return false;
                }

                // Only active tokens can be captured
                if (
                    token.status !== TOKEN_STATUS.ACTIVE
                ) {
                    return false;
                }

                // 🔥 IMPORTANT:
                // Tokens inside their finishing/home lane
                // can NEVER be captured.
                //
                // 51 = first home-lane cell
                // 52 = second
                // 53 = third
                // 54 = fourth
                // 55 = fifth
                // 56 = finished
                if (
                    token.step >= 51
                ) {
                    return false;
                }

                const opponentPosition =
                    getPlayerPathIndex(
                        token.color,
                        token.step
                    );

                return (
                    opponentPosition ===
                    globalPosition
                );
            });


        // --------------------------------------
        // OPPONENT BLOCK
        // --------------------------------------

        if (
            opponentTokens.length >= 2
        ) {

            return {
                tokens: currentTokens,
                captured: false,
            };

        }


        // --------------------------------------
        // NOTHING TO CAPTURE
        // --------------------------------------

        if (
            opponentTokens.length === 0
        ) {

            return {
                tokens: currentTokens,
                captured: false,
            };

        }


        // --------------------------------------
        // CAPTURE ONE OPPONENT
        // --------------------------------------

        const opponentIds =
            new Set(
                opponentTokens.map(
                    token => token.id
                )
            );


        const updatedTokens =
            currentTokens.map(token => {

                if (
                    !opponentIds.has(
                        token.id
                    )
                ) {
                    return token;
                }


                return {
                    ...token,

                    status:
                        TOKEN_STATUS.HOME,

                    step: -1,
                };

            });


        return {
            tokens: updatedTokens,
            captured: true,
        };
    };

    // ========================================
    // CHECK IF MOVE WILL CAPTURE
    // ========================================

    const willCaptureOpponent = (
        movedToken,
        targetStep,
        currentTokens
    ) => {

        // --------------------------------------
        // Only common path
        // --------------------------------------

        if (
            targetStep < 0 ||
            targetStep >= 51
        ) {
            return false;
        }


        const globalPosition =
            getPlayerPathIndex(
                movedToken.color,
                targetStep
            );


        if (
            globalPosition === null
        ) {
            return false;
        }


        // --------------------------------------
        // SAFE CELL
        // --------------------------------------

        if (
            isSafeCell(globalPosition)
        ) {
            return false;
        }


        // --------------------------------------
        // FIND OPPONENTS
        // --------------------------------------

        const opponentTokens =
            currentTokens.filter(token => {

                if (
                    token.id ===
                    movedToken.id
                ) {
                    return false;
                }


                if (
                    token.color ===
                    movedToken.color
                ) {
                    return false;
                }


                if (
                    token.status !==
                    TOKEN_STATUS.ACTIVE
                ) {
                    return false;
                }

                // 🔥 HOME / FINISHING LANE TOKENS
                // CANNOT BE KILLED

                if (
                    token.step >= 51
                ) {
                    return false;
                }


                const opponentPosition =
                    getPlayerPathIndex(
                        token.color,
                        token.step
                    );


                return (
                    opponentPosition ===
                    globalPosition
                );

            });


        // --------------------------------------
        // BLOCK CANNOT BE CAPTURED
        // --------------------------------------

        if (
            opponentTokens.length >= 2
        ) {
            return false;
        }


        return (
            opponentTokens.length === 1
        );
    };

    // ============================================
    // HARD COMPUTER AI
    // ============================================

    const getComputerTargetStep = (token) => {

        if (
            token.status === TOKEN_STATUS.HOME
        ) {
            return 0;
        }

        return token.step + dice;
    };


    // ============================================
    // CHECK IF MOVE FINISHES TOKEN
    // ============================================

    const computerCanFinish = (token) => {

        const targetStep =
            getComputerTargetStep(token);

        return targetStep === 56;
    };


    // ============================================
    // CHECK IF MOVE CAPTURES
    // ============================================

    const computerCanCapture = (token) => {

        const targetStep =
            getComputerTargetStep(token);

        if (
            token.status !==
            TOKEN_STATUS.ACTIVE
        ) {
            return false;
        }

        return willCaptureOpponent(
            token,
            targetStep,
            tokens
        );
    };


    // ============================================
    // CHECK SAFE CELL
    // ============================================

    const computerGetsSafeCell = (token) => {

        const targetStep =
            getComputerTargetStep(token);

        if (
            targetStep < 0 ||
            targetStep >= 51
        ) {
            return false;
        }

        const globalPath =
            getPlayerPathIndex(
                token.color,
                targetStep
            );

        if (
            globalPath === null
        ) {
            return false;
        }

        return isSafeCell(
            globalPath
        );
    };


    // ============================================
    // CHECK WHETHER TOKEN IS IN DANGER
    // ============================================

    const computerIsDangerousMove = (
        token
    ) => {

        const targetStep =
            getComputerTargetStep(token);

        if (
            token.status !==
            TOKEN_STATUS.ACTIVE
        ) {
            return false;
        }

        if (
            targetStep < 0 ||
            targetStep >= 51
        ) {
            return false;
        }

        const targetGlobal =
            getPlayerPathIndex(
                token.color,
                targetStep
            );

        if (
            targetGlobal === null
        ) {
            return false;
        }


        // Safe cells are protected
        if (
            isSafeCell(targetGlobal)
        ) {
            return false;
        }


        // Check every opponent
        return tokens.some(opponent => {

            if (
                opponent.color ===
                token.color
            ) {
                return false;
            }

            if (
                opponent.status !==
                TOKEN_STATUS.ACTIVE
            ) {
                return false;
            }

            const opponentGlobal =
                getPlayerPathIndex(
                    opponent.color,
                    opponent.step
                );

            if (
                opponentGlobal === null
            ) {
                return false;
            }


            const distance =
                (
                    targetGlobal -
                    opponentGlobal +
                    52
                ) % 52;


            return (
                distance >= 1 &&
                distance <= 6
            );

        });
    };

    // ============================================
    // HARD AI SCORE
    // ============================================

    const scoreComputerMove = (token) => {

        let score = 0;

        const targetStep =
            getComputerTargetStep(token);


        // ========================================
        // 1. FINISH
        // ========================================

        if (
            computerCanFinish(token)
        ) {
            score += 100000;
        }


        // ========================================
        // 2. CAPTURE
        // ========================================

        if (
            computerCanCapture(token)
        ) {
            score += 50000;
        }


        // ========================================
        // 3. SAFE / STAR
        // ========================================

        if (
            computerGetsSafeCell(token)
        ) {
            score += 10000;
        }


        // ========================================
        // 4. OPEN TOKEN
        // ========================================

        if (
            token.status ===
            TOKEN_STATUS.HOME &&
            dice === 6
        ) {
            score += 4000;
        }


        // ========================================
        // 5. MOVE ACTIVE TOKEN
        // ========================================

        if (
            token.status ===
            TOKEN_STATUS.ACTIVE
        ) {

            score +=
                targetStep * 100;
        }


        // ========================================
        // 6. GET CLOSER TO FINISH
        // ========================================

        if (
            token.status ===
            TOKEN_STATUS.ACTIVE
        ) {

            score +=
                targetStep * 20;
        }


        // ========================================
        // 7. DANGER PENALTY
        // ========================================

        if (
            computerIsDangerousMove(token)
        ) {
            score -= 15000;
        }


        return score;
    };


    // ============================================
    // SELECT BEST COMPUTER TOKEN
    // ============================================

    const chooseComputerToken = (
        validTokens
    ) => {

        if (
            !validTokens ||
            validTokens.length === 0
        ) {
            return null;
        }


        const scored =
            validTokens.map(token => ({

                token,

                score:
                    scoreComputerMove(
                        token
                    ),

            }));


        scored.sort(
            (a, b) =>
                b.score - a.score
        );


        return scored[0].token;
    };


    // ========================================
    // ANIMATE TOKEN
    // ========================================

    const animateToken = async (token) => {

        // --------------------------------------
        // START MOVEMENT
        // --------------------------------------

        setIsMoving(true);


        // --------------------------------------
        // CALCULATE TARGET STEP
        // --------------------------------------

        const targetStep =
            token.status === TOKEN_STATUS.HOME
                ? 0
                : token.step + dice;


        // --------------------------------------
        // CANNOT GO BEYOND FINISH
        // --------------------------------------

        if (targetStep > 56) {

            setIsMoving(false);

            return;
        }


        // --------------------------------------
        // TOKEN IS IN HOME
        // --------------------------------------

        if (
            token.status ===
            TOKEN_STATUS.HOME
        ) {

            // 🔊 TOKEN OPEN
            playSound("open");

            // Token enters player's own
            // starting position.

            updateTokenStep(
                token.id,
                0
            );


            // Small opening animation

            await new Promise(resolve =>
                setTimeout(
                    resolve,
                    350
                )
            );

        }


        // --------------------------------------
        // TOKEN IS ALREADY ACTIVE
        // --------------------------------------

        else {

            const startStep =
                token.step;


            // ----------------------------------
            // MOVE ONE CELL AT A TIME
            // ----------------------------------

            for (
                let step = startStep + 1;

                step <= targetStep;

                step++
            ) {

                // 🔊 RUN SOUND EVERY CELL
                playSound("run");

                updateTokenStep(
                    token.id,
                    step
                );


                await new Promise(resolve =>
                    setTimeout(
                        resolve,
                        180
                    )
                );

            }

        }


        // ======================================
        // CHECK CAPTURE
        // ======================================

        const capturedOpponent =
            willCaptureOpponent(
                token,
                targetStep,
                tokens
            );


        // ======================================
        // APPLY CAPTURE
        // ======================================

        if (
            capturedOpponent
        ) {

            // 🔊 KILL SOUND
            playSound("kill");

            setTokens(currentTokens => {

                const result =
                    captureOpponents(
                        token.id,
                        token.color,
                        targetStep,
                        currentTokens
                    );

                return result.tokens;

            });

        }


        // ======================================
        // CHECK FINISH
        // ======================================

        const finishingMove =
            targetStep === 56;


        // ======================================
        // MOVE TOKEN TO FINISHED STATE
        // ======================================

        if (
            finishingMove
        ) {

            // 🔊 ONE TOKEN FINISHED
            playSound("home");

            setTokens(currentTokens => {

                const updatedTokens =
                    currentTokens.map(item => {

                        if (
                            item.id !==
                            token.id
                        ) {
                            return item;
                        }


                        return {
                            ...item,

                            status:
                                TOKEN_STATUS.FINISHED,

                            // 57 is the finished
                            // state used by
                            // getTokenPosition()

                            step: 57,
                        };

                    });


                // --------------------------------
                // CHECK IF ALL 4 TOKENS FINISHED
                // --------------------------------

                const playerTokens =
                    updatedTokens.filter(item =>
                        item.color ===
                        token.color
                    );


                const finishedTokens =
                    playerTokens.filter(item =>
                        item.status ===
                        TOKEN_STATUS.FINISHED
                    );


                if (
                    finishedTokens.length === 4
                ) {

                    setWinner(
                        token.color
                    );

                }


                return updatedTokens;

            });

        }


        // ======================================
        // STOP IF PLAYER WON
        // ======================================
        //
        // Important:
        // We know this is a winning move when
        // the token reaches 56 and the player
        // already had 3 finished tokens before
        // this move.
        // ======================================

        const finishedBeforeMove =
            tokens.filter(
                item =>
                    item.color === token.color &&
                    item.status === TOKEN_STATUS.FINISHED
            ).length;

        const playerWon =
            finishingMove &&
            finishedBeforeMove === 3;

        if (playerWon) {

            setIsMoving(false);
            setHasRolled(false);

            if (onRollAvailabilityChange) {
                onRollAvailabilityChange(false);
            }

            return;
        }


        // ======================================
        // MOVEMENT FINISHED
        // ======================================

        setIsMoving(false);

        setHasRolled(false);


        // ======================================
        // EXTRA TURN
        // ======================================
        //
        // Player gets another turn when:
        //
        // 1. Dice = 6
        // 2. Opponent was captured
        // 3. Token reached finish
        // ======================================

        const extraTurn =
            dice === 6 ||
            capturedOpponent ||
            finishingMove;


        if (
            extraTurn
        ) {

            onRollAvailabilityChange(
                true
            );

            return;
        }


        // ======================================
        // NEXT PLAYER
        // ======================================

        const nextPlayer =
            getNextPlayer(
                currentPlayer
            );


        setCurrentPlayer(
            nextPlayer
        );


        onRollAvailabilityChange(
            true
        );

    };


    // ========================================
    // CLICK TOKEN
    // ========================================

    const moveToken = (
        tokenId
    ) => {

        if (isMoving) {
            return;
        }


        // ========================================
        // COMPUTER TURN
        // ========================================
        //
        // User cannot manually move
        // computer's green tokens.
        //

        const isComputerPlayer =
            gameMode?.computerPlayers?.includes(currentPlayer);

        if (isComputerPlayer) {
            return;
        }


        if (!hasRolled) {
            return;
        }


        const token =
            tokens.find(
                item =>
                    item.id === tokenId
            );


        if (!token) {
            return;
        }


        if (
            !canTokenMove(token)
        ) {
            return;
        }


        animateToken(token);
    };


    // ========================================
    // TOKEN VISUAL POSITION
    // ========================================

    const getTokenPosition = (
        token
    ) => {

        // ======================================
        // TOKEN IN HOME AREA
        // ======================================

        if (
            token.status ===
            TOKEN_STATUS.HOME
        ) {

            return HOME_POSITIONS[
                token.color
            ][
                token.tokenNumber - 1
            ];
        }


        // ======================================
        // FINISHED TOKEN
        // ======================================

        if (
            token.status ===
            TOKEN_STATUS.FINISHED
        ) {

            return {
                left: "50%",
                top: "50%",
            };
        }


        // ======================================
        // COMMON / HOME PATH
        // ======================================

        return getPlayerPathVisualPosition(
            token.color,
            token.step
        );
    };


    // ========================================
    // GET STACKED TOKEN POSITION
    // ========================================

    const getStackedTokenPosition = (token) => {

        const basePosition =
            getTokenPosition(token);

        if (!basePosition) {
            return null;
        }


        // ======================================
        // HOME / BASE TOKENS
        // ======================================

        if (
            token.status === TOKEN_STATUS.HOME
        ) {
            return basePosition;
        }


        // ========================================
        // FINISHED TOKENS
        // ========================================
        //
        // Finished tokens stay on their own
        // color's side of the center.
        // ========================================

        if (
            token.status === TOKEN_STATUS.FINISHED
        ) {

            const finishedTokens =
                tokens.filter(item =>
                    item.status ===
                    TOKEN_STATUS.FINISHED &&
                    item.color ===
                    token.color
                );


            const finishedIndex =
                finishedTokens.findIndex(
                    item =>
                        item.id === token.id
                );


            // ======================================
            // GREEN → LEFT
            // ======================================

            if (token.color === "green") {

                const positions = [

                    { left: "42%", top: "48%" },
                    { left: "42%", top: "52%" },
                    { left: "45%", top: "48%" },
                    { left: "45%", top: "52%" },

                ];

                const position =
                    positions[
                    finishedIndex %
                    positions.length
                    ];

                return position;
            }


            // ======================================
            // YELLOW → TOP
            // ======================================

            if (token.color === "yellow") {

                const positions = [

                    { left: "48%", top: "42%" },
                    { left: "52%", top: "42%" },
                    { left: "48%", top: "45%" },
                    { left: "52%", top: "45%" },

                ];

                const position =
                    positions[
                    finishedIndex %
                    positions.length
                    ];

                return position;
            }


            // ======================================
            // BLUE → RIGHT
            // ======================================

            if (token.color === "blue") {

                const positions = [

                    { left: "58%", top: "48%" },
                    { left: "58%", top: "52%" },
                    { left: "55%", top: "48%" },
                    { left: "55%", top: "52%" },

                ];

                const position =
                    positions[
                    finishedIndex %
                    positions.length
                    ];

                return position;
            }


            // ======================================
            // RED → BOTTOM
            // ======================================

            if (token.color === "red") {

                const positions = [

                    { left: "48%", top: "58%" },
                    { left: "52%", top: "58%" },
                    { left: "48%", top: "55%" },
                    { left: "52%", top: "55%" },

                ];

                const position =
                    positions[
                    finishedIndex %
                    positions.length
                    ];

                return position;
            }


            return basePosition;
        }


        // ======================================
        // HOME LANE
        // ======================================
        //
        // Home-lane cells are individual cells.
        // Do NOT stack them.
        // ======================================

        if (
            token.status === TOKEN_STATUS.ACTIVE &&
            token.step >= 51 &&
            token.step <= 55
        ) {
            return basePosition;
        }


        // ======================================
        // COMMON PATH
        // ======================================

        const globalPosition =
            getTokenGlobalPathIndex(token);


        if (
            globalPosition === null
        ) {
            return basePosition;
        }


        // ======================================
        // FIND TOKENS ON SAME COMMON CELL
        // ======================================

        const samePositionTokens =
            tokens.filter(otherToken => {

                const otherPosition =
                    getTokenGlobalPathIndex(
                        otherToken
                    );

                return (
                    otherPosition ===
                    globalPosition
                );

            });


        // --------------------------------------
        // Only one token
        // --------------------------------------

        if (
            samePositionTokens.length <= 1
        ) {
            return basePosition;
        }


        // ======================================
        // CURRENT PLAYER FIRST
        // ======================================

        const currentPlayerTokens =
            samePositionTokens.filter(
                item =>
                    item.color ===
                    currentPlayer
            );


        const otherTokens =
            samePositionTokens.filter(
                item =>
                    item.color !==
                    currentPlayer
            );


        const orderedTokens = [
            ...otherTokens,
            ...currentPlayerTokens,
        ];


        const stackIndex =
            orderedTokens.findIndex(
                item =>
                    item.id === token.id
            );


        // ======================================
        // COMMON PATH STACKING
        // ======================================

        const offsets = [

            {
                x: -0.55,
                y: 0,
            },

            {
                x: 0.55,
                y: 0,
            },

            {
                x: 0,
                y: -0.55,
            },

            {
                x: 0,
                y: 0.55,
            },

        ];


        const offset =
            offsets[
            stackIndex %
            offsets.length
            ];


        return {

            left:
                `calc(${basePosition.left} + ${offset.x}%)`,

            top:
                `calc(${basePosition.top} + ${offset.y}%)`,

        };

    };

    const resetGame = () => {

        // ==================================
        // REMOVE OLD SAVED GAME
        // ==================================

        localStorage.removeItem(
            "ludo_game_state"
        );


        // ==================================
        // RESET BOARD
        // ==================================

        setTokens(
            createInitialTokens()
        );

        setCurrentPlayer(
            activePlayers[0] ||
            TURN_ORDER[0]
        );

        setHasRolled(false);

        setIsMoving(false);

        setWinner(null);

        onRollAvailabilityChange(
            true
        );
    };


    // ========================================
    // SEND BOARD STATE TO APP
    // ========================================

    useEffect(() => {

        if (!onGameStateChange) {
            return;
        }


        onGameStateChange({

            tokens,

            currentPlayer,

            hasRolled,

            winner,

        });

    }, [
        tokens,
        currentPlayer,
        hasRolled,
        winner,
        onGameStateChange,
    ]);


    // ============================================
    // COMPUTER AUTO MOVE
    // ============================================
    //
    // Any player included in
    // gameMode.computerPlayers is controlled
    // by computer.
    //
    // 2 Player:
    // blue = You
    // green = Computer
    //
    // 3 Player:
    // blue = You
    // green/yellow = Computer
    //
    // 4 Player:
    // blue = You
    // green/yellow/red = Computer
    //
    // Offline:
    // computerPlayers = []
    // so nobody is controlled by computer.
    // ============================================

    useEffect(() => {

        if (!gameMode) {
            return;
        }

        // Check current player
        const isComputerPlayer =
            gameMode?.computerPlayers?.includes(
                currentPlayer
            );

        // Human player
        if (!isComputerPlayer) {
            return;
        }

        // Dice must be rolled
        if (!hasRolled) {
            return;
        }

        // Already moving
        if (isMoving) {
            return;
        }

        // Game finished
        if (winner) {
            return;
        }

        const validTokens =
            getValidTokens();

        // No valid token
        if (
            !validTokens ||
            validTokens.length === 0
        ) {
            return;
        }

        // One valid token
        // Existing auto-move effect handles it
        if (validTokens.length === 1) {
            return;
        }

        // ========================================
        // COMPUTER THINKING
        // ========================================

        const timer = setTimeout(() => {

            const currentValidTokens =
                getValidTokens();

            if (
                !currentValidTokens ||
                currentValidTokens.length === 0
            ) {
                return;
            }

            const selectedToken =
                chooseComputerToken(
                    currentValidTokens
                );

            if (selectedToken) {

                animateToken(
                    selectedToken
                );

            }

        }, 700);

        return () => {
            clearTimeout(timer);
        };

    }, [
        gameMode,
        currentPlayer,
        hasRolled,
        isMoving,
        winner,
        dice,
        tokens,
    ]);


    // ========================================
    // AUTO PLAY - PLAYER ONLY
    // ========================================

    useEffect(() => {

        // ========================================
        // CHECK COMPUTER
        // ========================================

        const isComputerPlayer =
            gameMode?.computerPlayers?.includes(
                currentPlayer
            );

        // ========================================
        // COMPUTER
        // ========================================
        //
        // Computer is handled by the
        // COMPUTER AUTO MOVE effect above.
        //
        // Do NOT use autoPlay for computer.
        //

        if (isComputerPlayer) {
            return;
        }

        // ========================================
        // HUMAN PLAYER
        // ========================================
        //
        // Human only auto moves when
        // autoPlay is ON.
        //

        if (!autoPlay) {
            return;
        }

        if (!hasRolled) {
            return;
        }

        if (isMoving) {
            return;
        }

        if (winner) {
            return;
        }

        const validTokens =
            getValidTokens();

        if (
            !validTokens ||
            validTokens.length === 0
        ) {
            return;
        }

        // ========================================
        // WAIT 3 SECONDS
        // ========================================

        const timer = setTimeout(() => {

            const currentValidTokens =
                getValidTokens();

            if (
                !currentValidTokens ||
                currentValidTokens.length === 0
            ) {
                return;
            }

            // Human player:
            // choose first available token
            const selectedToken =
                currentValidTokens[0];

            if (selectedToken) {

                animateToken(
                    selectedToken
                );

            }

        }, 3000);

        return () => {
            clearTimeout(timer);
        };

    }, [
        autoPlay,
        hasRolled,
        isMoving,
        winner,
        dice,
        currentPlayer,
        tokens,
        gameMode,
    ]);


    const winningPrize = betAmount;
    const { grossWinning, taxAmount, netWinning } = calculateLudoWinning(winningPrize);


    // ========================================
    // RENDER
    // ========================================

    return (
        <>
            <div className="ludo-game-wrapper">

                {/* ====================================
                    TURN INDICATOR
                ==================================== */}


                {/* ====================================
                    BOARD
                ==================================== */}

                <div className="ludo-container">

                    <img
                        src="/assets/board.png"
                        alt="Ludo Board"
                        className="ludo-board"
                    />


                    {/* ==================================
                    TOKENS
                ================================== */}

                    {tokens
                        .filter(token =>
                            activePlayers.includes(token.color)
                        )
                        .map(token => {

                            const position =
                                getStackedTokenPosition(token);

                            const canMove =
                                canTokenMove(token);

                            return (
                                <Token
                                    key={token.id}
                                    color={token.color}
                                    position={position}
                                    onClick={() =>
                                        moveToken(token.id)
                                    }
                                    canMove={canMove}
                                    isCurrentPlayer={
                                        token.color === currentPlayer
                                    }
                                />
                            );

                        })}

                </div>

            </div>

            {winner &&
                createPortal(
                    <div
                        className={`ludo-result-overlay ${winner === "blue"
                            ? "result-win"
                            : "result-lose"
                            }`}
                    >

                        <div className="ludo-result-modal">

                            {winner === "blue" ? (

                                <>
                                    <div className="result-icon win-icon">
                                        🏆
                                    </div>

                                    <h2 className="result-title">
                                        YOU WIN!
                                    </h2>

                                    <p className="result-player">
                                        Congratulations! 🎊
                                    </p>

                                    <div className="result-prize">
                                        <span>Winning Prize</span>

                                        <strong>
                                            ₹{Number(betAmount || 0).toLocaleString()}
                                        </strong>
                                    </div>

                                    <div className="result-tax">
                                        <span>Tax Deduction (10%)</span>

                                        <strong>
                                            - ₹{Number(taxAmount || 0).toLocaleString()}
                                        </strong>
                                    </div>

                                    <div className="result-net">
                                        <span>You Receive</span>

                                        <strong>
                                            ₹{Number(netWinning || 0).toLocaleString()}
                                        </strong>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={onPlayAgain}
                                        className="result-play-again lose-button mt-3"
                                    >
                                        🎮 Play Again
                                    </button>
                                </>

                            ) : (

                                <>
                                    <div className="result-icon lose-icon">
                                        😔
                                    </div>

                                    <h2 className="result-title lose-title">
                                        YOU LOSE
                                    </h2>

                                    {/* COMPUTER WINNER PROFILE */}
                                    <div className="result-winner-player">

                                        {playerProfiles?.[winner]?.image && (
                                            <img
                                                src={
                                                    playerProfiles[winner].image
                                                }
                                                alt={
                                                    playerProfiles[winner]?.name ||
                                                    "Opponent"
                                                }
                                                className="result-winner-avatar"
                                            />
                                        )}

                                        <div className="result-winner-info">

                                            <span>
                                                WINNER
                                            </span>

                                            <strong>
                                                {playerProfiles?.[winner]?.name ||
                                                    "Opponent"}
                                            </strong>

                                        </div>

                                    </div>

                                    <p className="result-description">
                                        Better luck next time!
                                        <br />

                                        All 4 tokens of{" "}
                                        <strong>
                                            {playerProfiles?.[winner]?.name ||
                                                "Opponent"}
                                        </strong>{" "}
                                        reached home first.
                                    </p>

                                    <div className="result-loss">

                                        <span>
                                            Entry Amount
                                        </span>

                                        <strong>
                                            ₹{Number(
                                                entryBet || 0
                                            ).toLocaleString()}
                                        </strong>

                                    </div>

                                    <button
                                        type="button"
                                        onClick={onPlayAgain}
                                        className="result-play-again lose-button"
                                    >
                                        🔄 Try Again
                                    </button>

                                </>
                            )}

                        </div>

                    </div>,

                    document.querySelector(
                        ".ludo-page .game-area"
                    )
                )
            }
        </>
    );
}


export default LudoBoard;